(() => {
  'use strict';

  const GLOBAL_KEY = '__LP_CONVERSIONS_HEADER_V1__';

  if (window[GLOBAL_KEY]) {
    window[GLOBAL_KEY].init(document);
    return;
  }

  const state = {
    instances: new Map(),
    active: null,
    initialized: false,
    init
  };

  window[GLOBAL_KEY] = state;

  class LPConversionsHeader {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId || '';
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.config = this.readConfig();
      this.cart = null;
      this.cartPromise = null;
      this.busyKeys = new Set();
      this.lastFocus = null;
      this.lastCartFetch = 0;
      this.statusTimer = null;

      this.overlay = root.querySelector('[data-lpch-overlay]');
      this.menu = root.querySelector('[data-lpch-menu]');
      this.cartDrawer = root.querySelector('[data-lpch-cart]');
      this.menuOpenButton = root.querySelector('[data-lpch-menu-open]');
      this.cartOpenButton = root.querySelector('[data-lpch-cart-open]');
      this.loading = root.querySelector('[data-lpch-loading]');
      this.empty = root.querySelector('[data-lpch-empty]');
      this.filled = root.querySelector('[data-lpch-filled]');
      this.items = root.querySelector('[data-lpch-items]');
      this.status = root.querySelector('[data-lpch-status]');
      this.subtotal = root.querySelector('[data-lpch-subtotal]');
      this.subtotalLabel = root.querySelector('[data-lpch-subtotal-label]');
      this.checkoutTotal = root.querySelector('[data-lpch-checkout-total]');
      this.checkoutButton = root.querySelector('[data-lpch-checkout]');
      this.countNodes = root.querySelectorAll('[data-lpch-count]');

      this.portal = this.mountPortal();
      this.stateHost = this.portal || this.root;

      this.onRootClick = this.onRootClick.bind(this);
      this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
      this.onProductSubmit = this.onProductSubmit.bind(this);
      this.onExternalCartEvent = this.onExternalCartEvent.bind(this);

      this.bind();
      this.configureStandardActions();
      this.publishApi();
    }

    readConfig() {
      const node = this.root.querySelector('[data-lpch-config]');

      if (!node) return {};

      try {
        return JSON.parse(node.textContent || '{}');
      } catch (error) {
        console.error(
          '[LP Conversions Header] Invalid configuration.',
          error
        );

        return {};
      }
    }

    mountPortal() {
      if (
        !this.overlay ||
        !this.menu ||
        !this.cartDrawer ||
        !document.body
      ) {
        return null;
      }

      const portal = document.createElement('div');

      portal.className = 'lpch lpch__portal';
      portal.dataset.lpchPortal = this.sectionId;
      portal.setAttribute(
        'style',
        this.root.getAttribute('style') || ''
      );

      portal.append(
        this.overlay,
        this.menu,
        this.cartDrawer
      );

      document.body.appendChild(portal);

      return portal;
    }

    bind() {
      this.root.addEventListener(
        'click',
        this.onRootClick,
        { signal: this.signal }
      );

      this.portal?.addEventListener(
        'click',
        this.onRootClick,
        { signal: this.signal }
      );

      document.addEventListener(
        'keydown',
        this.onDocumentKeydown,
        { signal: this.signal }
      );

      document.addEventListener(
        'submit',
        this.onProductSubmit,
        {
          capture: true,
          signal: this.signal
        }
      );

      [
        'shopify:cart:lines-update',
        'cart:updated',
        'cart:update',
        'cart:refresh',
        'lp:cart:refresh'
      ].forEach((eventName) => {
        document.addEventListener(
          eventName,
          this.onExternalCartEvent,
          { signal: this.signal }
        );
      });

      window.addEventListener(
        'pageshow',
        () => this.refresh(false),
        { signal: this.signal }
      );
    }

    destroy() {
      this.abortController.abort();
      window.clearTimeout(this.statusTimer);

      if (state.active === this) {
        state.active = null;
      }

      if (
        document.body.classList.contains(
          'lpch-scroll-locked'
        )
      ) {
        this.unlockBody();
      }

      this.portal?.remove();
    }

    publishApi() {
      state.active = this;

      window.LPConversionsCart = {
        open: () => this.openCart(),
        close: () => this.closeDrawers(),
        refresh: () => this.refresh(true),

        add: (
          idOrItems,
          quantity = 1,
          properties = undefined
        ) => {
          const items = Array.isArray(idOrItems)
            ? idOrItems
            : [
                {
                  id: idOrItems,
                  quantity,
                  ...(properties
                    ? { properties }
                    : {})
                }
              ];

          return this.addItems(items, true);
        }
      };
    }

    configureStandardActions() {
      const configure = () => {
        const action =
          window.Shopify?.actions?.openCart;

        if (
          !action ||
          typeof action.configure !== 'function'
        ) {
          return false;
        }

        try {
          action.configure({
            handler: () => this.openCart()
          });

          return true;
        } catch (error) {
          return false;
        }
      };

      if (!configure()) {
        window.setTimeout(configure, 250);
        window.setTimeout(configure, 1000);
      }
    }

    onRootClick(event) {
      const menuOpen = event.target.closest(
        '[data-lpch-menu-open]'
      );

      if (menuOpen) {
        event.preventDefault();
        this.openMenu(menuOpen);
        return;
      }

      const cartOpen = event.target.closest(
        '[data-lpch-cart-open]'
      );

      if (cartOpen) {
        event.preventDefault();
        this.openCart(cartOpen);
        return;
      }

      if (
        event.target.closest('[data-lpch-close]') ||
        event.target.closest('[data-lpch-overlay]')
      ) {
        event.preventDefault();
        this.closeDrawers();
        return;
      }

      const changeButton = event.target.closest(
        '[data-lpch-change]'
      );

      if (changeButton) {
        event.preventDefault();

        const key = changeButton.dataset.key;
        const quantity = Number(
          changeButton.dataset.quantity
        );

        this.changeLine(key, quantity);
        return;
      }

      const removeButton = event.target.closest(
        '[data-lpch-remove]'
      );

      if (removeButton) {
        event.preventDefault();

        this.changeLine(
          removeButton.dataset.key,
          0
        );

        return;
      }

      const drawerLink = event.target.closest(
        [
          '.lpch__mobile-link',
          '.lpch__empty-link',
          '.lpch__cart-title',
          '.lpch__cart-media'
        ].join(', ')
      );

      if (drawerLink) {
        this.closeDrawers(false);
      }
    }

    onDocumentKeydown(event) {
      if (
        event.key === 'Escape' &&
        this.isOpen()
      ) {
        event.preventDefault();
        this.closeDrawers();
        return;
      }

      if (
        event.key !== 'Tab' ||
        !this.isOpen()
      ) {
        return;
      }

      const panel =
        this.stateHost.classList.contains(
          'is-cart-open'
        )
          ? this.cartDrawer
          : this.menu;

      if (!panel) return;

      const focusable = Array.from(
        panel.querySelectorAll(
          [
            'a[href]',
            'button:not([disabled])',
            'input:not([disabled])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            '[tabindex]:not([tabindex="-1"])'
          ].join(', ')
        )
      ).filter((element) => {
        return (
          !element.hidden &&
          element.offsetParent !== null
        );
      });

      if (!focusable.length) return;

      const first = focusable[0];
      const last =
        focusable[focusable.length - 1];

      if (
        event.shiftKey &&
        document.activeElement === first
      ) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        document.activeElement === last
      ) {
        event.preventDefault();
        first.focus();
      }
    }

    onExternalCartEvent(event) {
      if (
        event.type ===
          'shopify:cart:lines-update' &&
        event.promise
      ) {
        Promise.resolve(event.promise).finally(
          () => this.refresh(true)
        );

        return;
      }

      this.refresh(true);
    }

    async onProductSubmit(event) {
      if (
        !this.config.ajaxProductForms ||
        this.config.designMode
      ) {
        return;
      }

      const form = event.target;

      if (!(form instanceof HTMLFormElement)) {
        return;
      }

      if (
        form.matches(
          '[data-lpch-native-cart], [data-no-ajax-cart]'
        )
      ) {
        return;
      }

      const action = this.normaliseAddUrl(
        form.action ||
          form.getAttribute('action') ||
          ''
      );

      if (!action) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const submitter =
        event.submitter instanceof HTMLElement
          ? event.submitter
          : form.querySelector('[type="submit"]');

      const originalText =
        submitter?.textContent;

      if (submitter) {
        submitter.setAttribute(
          'aria-busy',
          'true'
        );

        submitter.setAttribute(
          'disabled',
          'disabled'
        );
      }

      this.setStatus(
        this.config.loadingText ||
          'Updating your cart…',
        false
      );

      try {
        let formData;

        try {
          formData = event.submitter
            ? new FormData(
                form,
                event.submitter
              )
            : new FormData(form);
        } catch (error) {
          formData = new FormData(form);
        }

        const response = await fetch(action, {
          method: 'POST',
          headers: {
            Accept: 'application/json'
          },
          body: formData,
          credentials: 'same-origin'
        });

        const payload =
          await this.readResponse(response);

        if (!response.ok) {
          throw new Error(
            payload.description ||
              payload.message ||
              this.config.errorText
          );
        }

        const cart = await this.refresh(true);

        this.setStatus(
          this.config.addedText ||
            'Added to your cart.',
          false
        );

        this.dispatchCartUpdate(
          cart,
          'add'
        );

        if (this.config.openAfterAdd) {
          this.openCart(submitter, false);
        }
      } catch (error) {
        this.setStatus(
          error.message ||
            this.config.errorText ||
            'Something went wrong. Please try again.',
          true
        );
      } finally {
        if (submitter) {
          submitter.removeAttribute(
            'aria-busy'
          );

          submitter.removeAttribute(
            'disabled'
          );

          if (
            originalText !== undefined &&
            submitter.textContent !== originalText
          ) {
            submitter.textContent =
              originalText;
          }
        }
      }
    }

    normaliseAddUrl(value) {
      if (!value) return null;

      try {
        const url = new URL(
          value,
          window.location.origin
        );

        if (
          !/\/cart\/add(?:\.js)?\/?$/.test(
            url.pathname
          )
        ) {
          return null;
        }

        url.pathname = url.pathname.replace(
          /\/cart\/add(?:\.js)?\/?$/,
          '/cart/add.js'
        );

        return url.toString();
      } catch (error) {
        return null;
      }
    }

    async addItems(
      items,
      openAfter = true
    ) {
      const root =
        this.config.rootUrl ||
        window.Shopify?.routes?.root ||
        '/';

      const response = await fetch(
        `${root}cart/add.js`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type':
              'application/json'
          },
          credentials: 'same-origin',
          body: JSON.stringify({ items })
        }
      );

      const payload =
        await this.readResponse(response);

      if (!response.ok) {
        throw new Error(
          payload.description ||
            payload.message ||
            this.config.errorText
        );
      }

      const cart = await this.refresh(true);

      this.dispatchCartUpdate(
        cart,
        'add'
      );

      if (openAfter) {
        this.openCart(null, false);
      }

      return cart;
    }

    async changeLine(key, quantity) {
      if (
        !key ||
        !Number.isFinite(quantity) ||
        this.busyKeys.has(key)
      ) {
        return;
      }

      this.busyKeys.add(key);
      this.setLineBusy(key, true);

      this.setStatus(
        this.config.loadingText ||
          'Updating your cart…',
        false
      );

      try {
        const root =
          this.config.rootUrl ||
          window.Shopify?.routes?.root ||
          '/';

        const response = await fetch(
          `${root}cart/change.js`,
          {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type':
                'application/json'
            },
            credentials: 'same-origin',
            body: JSON.stringify({
              id: key,
              quantity
            })
          }
        );

        const cart =
          await this.readResponse(response);

        if (!response.ok) {
          throw new Error(
            cart.description ||
              cart.message ||
              this.config.errorText
          );
        }

        this.cart = cart;
        this.lastCartFetch = Date.now();

        this.render(cart);
        this.setStatus('', false);

        this.dispatchCartUpdate(
          cart,
          quantity === 0
            ? 'remove'
            : 'update'
        );
      } catch (error) {
        this.setStatus(
          error.message ||
            this.config.errorText ||
            'Something went wrong. Please try again.',
          true
        );
      } finally {
        this.busyKeys.delete(key);
        this.setLineBusy(key, false);
      }
    }

    setLineBusy(key, busy) {
      this.stateHost
        .querySelectorAll(
          `[data-key="${CSS.escape(key)}"] button`
        )
        .forEach((button) => {
          button.disabled = busy;
        });
    }

    async refresh(force = false) {
      if (
        !force &&
        this.cart &&
        Date.now() - this.lastCartFetch < 5000
      ) {
        return this.cart;
      }

      if (this.cartPromise) {
        return this.cartPromise;
      }

      const root =
        this.config.rootUrl ||
        window.Shopify?.routes?.root ||
        '/';

      const separator = root.includes('?')
        ? '&'
        : '?';

      this.cartPromise = fetch(
        `${root}cart.js${separator}_lpch=${Date.now()}`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json'
          },
          credentials: 'same-origin',
          cache: 'no-store'
        }
      )
        .then(async (response) => {
          const cart =
            await this.readResponse(response);

          if (!response.ok) {
            throw new Error(
              cart.description ||
                cart.message ||
                this.config.errorText
            );
          }

          this.cart = cart;
          this.lastCartFetch = Date.now();

          this.render(cart);

          return cart;
        })
        .catch((error) => {
          this.setStatus(
            error.message ||
              this.config.errorText ||
              'Something went wrong. Please try again.',
            true
          );

          throw error;
        })
        .finally(() => {
          this.cartPromise = null;

          if (this.loading) {
            this.loading.hidden = true;
          }
        });

      return this.cartPromise;
    }

    async readResponse(response) {
      const contentType =
        response.headers.get(
          'content-type'
        ) || '';

      if (
        contentType.includes(
          'application/json'
        )
      ) {
        return response.json();
      }

      const text = await response.text();

      try {
        return JSON.parse(text);
      } catch (error) {
        return {
          description: text
        };
      }
    }

    render(cart) {
      if (!cart) return;

      this.updateCount(
        cart.item_count || 0
      );

      const isEmpty =
        !cart.items ||
        cart.items.length === 0;

      if (this.empty) {
        this.empty.hidden = !isEmpty;
      }

      if (this.filled) {
        this.filled.hidden = isEmpty;
      }

      if (isEmpty) {
        if (this.items) {
          this.items.replaceChildren();
        }

        return;
      }

      if (this.items) {
        const fragment =
          document.createDocumentFragment();

        cart.items.forEach((item) => {
          fragment.appendChild(
            this.createCartItem(item)
          );
        });

        this.items.replaceChildren(fragment);
      }

      const subtotal = this.formatMoney(
        cart.total_price || 0
      );

      const count =
        cart.item_count || 0;

      const itemWord =
        count === 1
          ? this.config.itemSingular ||
            'item'
          : this.config.itemPlural ||
            'items';

      if (this.subtotal) {
        this.subtotal.textContent =
          subtotal;
      }

      if (this.checkoutTotal) {
        this.checkoutTotal.textContent =
          subtotal;
      }

      if (this.subtotalLabel) {
        this.subtotalLabel.textContent =
          `${
            this.config.subtotalLabel ||
            'Subtotal'
          } (${count} ${itemWord})`;
      }

      if (this.checkoutButton) {
        this.checkoutButton.href =
          this.config.checkoutUrl ||
          '/checkout';
      }
    }

    createCartItem(item) {
      const article =
        document.createElement('article');

      article.className =
        'lpch__cart-item';

      article.dataset.key = item.key;

      const media =
        document.createElement('a');

      media.className =
        'lpch__cart-media';

      media.href =
        item.url ||
        this.config.cartUrl ||
        '/cart';

      media.setAttribute(
        'aria-label',
        item.product_title ||
          item.title ||
          'Cart item'
      );

      const imageUrl =
        item.featured_image?.url ||
        item.image;

      if (imageUrl) {
        const image =
          document.createElement('img');

        image.src = this.resizeImage(
          imageUrl,
          180
        );

        image.alt =
          item.featured_image?.alt ||
          item.product_title ||
          '';

        image.width = 88;
        image.height = 104;
        image.loading = 'lazy';

        media.appendChild(image);
      }

      const main =
        document.createElement('div');

      main.className =
        'lpch__cart-item-main';

      const info =
        document.createElement('div');

      info.className =
        'lpch__cart-info';

      const title =
        document.createElement('a');

      title.className =
        'lpch__cart-title';

      title.href =
        item.url ||
        this.config.cartUrl ||
        '/cart';

      title.textContent =
        item.product_title ||
        item.title ||
        'Product';

      info.appendChild(title);

      if (
        item.variant_title &&
        item.variant_title !==
          'Default Title'
      ) {
        const variant =
          document.createElement('p');

        variant.className =
          'lpch__cart-variant';

        variant.textContent =
          item.variant_title;

        info.appendChild(variant);
      }

      const price =
        document.createElement('strong');

      price.className =
        'lpch__cart-price';

      price.textContent = this.formatMoney(
        item.final_line_price ??
          item.line_price ??
          0
      );

      const controls =
        document.createElement('div');

      controls.className =
        'lpch__cart-controls';

      const quantity =
        document.createElement('div');

      quantity.className =
        'lpch__quantity';

      quantity.setAttribute(
        'aria-label',
        `Quantity for ${
          item.product_title ||
          item.title ||
          'item'
        }`
      );

      const minus = this.quantityButton(
        '−',
        this.config.decreaseLabel ||
          'Decrease quantity',
        item.key,
        Math.max(
          0,
          item.quantity - 1
        )
      );

      const number =
        document.createElement('span');

      number.textContent =
        String(item.quantity);

      number.setAttribute(
        'aria-live',
        'polite'
      );

      const plus = this.quantityButton(
        '+',
        this.config.increaseLabel ||
          'Increase quantity',
        item.key,
        item.quantity + 1
      );

      quantity.append(
        minus,
        number,
        plus
      );

      const remove =
        document.createElement('button');

      remove.type = 'button';
      remove.className =
        'lpch__remove';

      remove.dataset.lpchRemove = '';
      remove.dataset.key = item.key;

      remove.setAttribute(
        'aria-label',
        `${
          this.config.removeLabel ||
          'Remove'
        } ${
          item.product_title ||
          item.title ||
          'item'
        }`
      );

      remove.innerHTML = `
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
        >
          <path
            d="M4 7h16M9 7V4h6v3M7 7l1 14h8l1-14M10 11v6M14 11v6"
          />
        </svg>
      `;

      const removeText =
        document.createElement('span');

      removeText.textContent =
        this.config.removeLabel ||
        'Remove';

      remove.appendChild(removeText);

      controls.append(
        quantity,
        remove
      );

      main.append(
        info,
        price,
        controls
      );

      article.append(
        media,
        main
      );

      return article;
    }

    quantityButton(
      text,
      label,
      key,
      quantity
    ) {
      const button =
        document.createElement('button');

      button.type = 'button';
      button.textContent = text;
      button.dataset.lpchChange = '';
      button.dataset.key = key;
      button.dataset.quantity =
        String(quantity);

      button.setAttribute(
        'aria-label',
        label
      );

      return button;
    }

    resizeImage(value, width) {
      try {
        const url = new URL(
          value,
          window.location.origin
        );

        url.searchParams.set(
          'width',
          String(width)
        );

        return url.toString();
      } catch (error) {
        return value;
      }
    }

    formatMoney(cents) {
      const currency =
        this.config.currency ||
        'GBP';

      const locale =
        this.config.locale ||
        document.documentElement.lang ||
        'en-GB';

      try {
        return new Intl.NumberFormat(
          locale,
          {
            style: 'currency',
            currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }
        ).format(
          Number(cents || 0) / 100
        );
      } catch (error) {
        return `${currency} ${(
          Number(cents || 0) / 100
        ).toFixed(2)}`;
      }
    }

    updateCount(count) {
      document
        .querySelectorAll(
          '[data-lpch-count]'
        )
        .forEach((node) => {
          node.textContent =
            String(count);

          node.classList.toggle(
            'is-hidden',
            Boolean(
              this.config.hideZeroCount &&
              count === 0
            )
          );
        });
    }

    setStatus(
      message,
      isError = false
    ) {
      if (!this.status) return;

      window.clearTimeout(
        this.statusTimer
      );

      this.status.textContent =
        message || '';

      this.status.classList.toggle(
        'is-error',
        Boolean(isError)
      );

      if (
        message &&
        !isError
      ) {
        this.statusTimer =
          window.setTimeout(() => {
            if (this.status) {
              this.status.textContent = '';
            }
          }, 3000);
      }
    }

    openMenu(trigger = null) {
      this.lastFocus =
        trigger ||
        document.activeElement;

      this.root.classList.remove(
        'is-cart-open'
      );

      this.root.classList.add(
        'is-menu-open'
      );

      this.stateHost.classList.remove(
        'is-cart-open'
      );

      this.stateHost.classList.add(
        'is-menu-open'
      );

      this.setExpanded(
        this.menuOpenButton,
        true
      );

      this.setExpanded(
        this.cartOpenButton,
        false
      );

      this.setAriaHidden(
        this.menu,
        false
      );

      this.setAriaHidden(
        this.cartDrawer,
        true
      );

      this.lockBody();

      requestAnimationFrame(() => {
        this.menu
          ?.querySelector('a, button')
          ?.focus();
      });
    }

    async openCart(
      trigger = null,
      refresh = true
    ) {
      this.lastFocus =
        trigger ||
        this.lastFocus ||
        document.activeElement;

      this.root.classList.remove(
        'is-menu-open'
      );

      this.root.classList.add(
        'is-cart-open'
      );

      this.stateHost.classList.remove(
        'is-menu-open'
      );

      this.stateHost.classList.add(
        'is-cart-open'
      );

      this.setExpanded(
        this.menuOpenButton,
        false
      );

      this.setExpanded(
        this.cartOpenButton,
        true
      );

      this.setAriaHidden(
        this.menu,
        true
      );

      this.setAriaHidden(
        this.cartDrawer,
        false
      );

      this.lockBody();

      if (
        this.loading &&
        !this.cart
      ) {
        this.loading.hidden = false;
      }

      requestAnimationFrame(() => {
        this.cartDrawer
          ?.querySelector(
            '[data-lpch-close]'
          )
          ?.focus();
      });

      if (refresh) {
        try {
          await this.refresh(true);
        } catch (error) {
          // Error is already shown.
        }
      }
    }

    closeDrawers(
      restoreFocus = true
    ) {
      const wasOpen = this.isOpen();

      this.root.classList.remove(
        'is-menu-open',
        'is-cart-open'
      );

      this.stateHost.classList.remove(
        'is-menu-open',
        'is-cart-open'
      );

      this.setExpanded(
        this.menuOpenButton,
        false
      );

      this.setExpanded(
        this.cartOpenButton,
        false
      );

      this.setAriaHidden(
        this.menu,
        true
      );

      this.setAriaHidden(
        this.cartDrawer,
        true
      );

      this.unlockBody();

      if (
        wasOpen &&
        restoreFocus &&
        this.lastFocus instanceof HTMLElement
      ) {
        requestAnimationFrame(() => {
          this.lastFocus?.focus();
        });
      }
    }

    isOpen() {
      return (
        this.stateHost.classList.contains(
          'is-menu-open'
        ) ||
        this.stateHost.classList.contains(
          'is-cart-open'
        )
      );
    }

    setExpanded(element, value) {
      if (element) {
        element.setAttribute(
          'aria-expanded',
          String(value)
        );
      }
    }

    setAriaHidden(element, value) {
      if (element) {
        element.setAttribute(
          'aria-hidden',
          String(value)
        );
      }
    }

    lockBody() {
      if (
        document.body.classList.contains(
          'lpch-scroll-locked'
        )
      ) {
        return;
      }

      const scrollbar =
        window.innerWidth -
        document.documentElement.clientWidth;

      document.body.dataset.lpchPaddingRight =
        document.body.style.paddingRight ||
        '';

      if (scrollbar > 0) {
        document.body.style.paddingRight =
          `${scrollbar}px`;
      }

      document.body.classList.add(
        'lpch-scroll-locked'
      );
    }

    unlockBody() {
      document.body.classList.remove(
        'lpch-scroll-locked'
      );

      document.body.style.paddingRight =
        document.body.dataset
          .lpchPaddingRight || '';

      delete document.body.dataset
        .lpchPaddingRight;
    }

    dispatchCartUpdate(cart, action) {
      document.dispatchEvent(
        new CustomEvent(
          'lp:cart:updated',
          {
            detail: {
              cart,
              action,
              source:
                'lp-conversions-header-v1'
            }
          }
        )
      );
    }
  }

  function init(scope = document) {
    scope
      .querySelectorAll?.('[data-lpch]')
      .forEach((root) => {
        const sectionId =
          root.dataset.sectionId ||
          root.id;

        const existing =
          state.instances.get(sectionId);

        if (
          existing?.root === root
        ) {
          return;
        }

        existing?.destroy();

        const instance =
          new LPConversionsHeader(root);

        state.instances.set(
          sectionId,
          instance
        );

        state.active = instance;
      });
  }

  function boot() {
    init(document);

    if (state.initialized) return;

    state.initialized = true;

    document.addEventListener(
      'shopify:section:load',
      (event) => init(event.target)
    );

    document.addEventListener(
      'shopify:section:unload',
      (event) => {
        const root =
          event.target.querySelector?.(
            '[data-lpch]'
          ) ||
          (
            event.target.matches?.(
              '[data-lpch]'
            )
              ? event.target
              : null
          );

        if (!root) return;

        const sectionId =
          root.dataset.sectionId ||
          root.id;

        state.instances
          .get(sectionId)
          ?.destroy();

        state.instances.delete(
          sectionId
        );
      }
    );
  }

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      boot,
      { once: true }
    );
  } else {
    boot();
  }
})();